import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projects } from '../data/projects';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const ProjectDetail = () => {
    const { id } = useParams();
    const { t, language } = useLanguage();
    const { theme } = useTheme();

    // Find the project that matches the ID from the URL
    const currentIndex = projects.findIndex(p => p.id === parseInt(id));
    const project = projects[currentIndex];

    // Determine the next and previous projects
    const nextProject = currentIndex === projects.length - 1 ? projects[0] : projects[currentIndex + 1];
    const prevProject = currentIndex === 0 ? projects[projects.length - 1] : projects[currentIndex - 1];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!project) {
        return <div className="page-content">Project not found</div>;
    }

    // Helper to get localized string
    const getVal = (en, no) => (language === 'no' ? (no || en) : en);

    const category = getVal(project.category, project.categoryNo);
    const description = getVal(project.description, project.descriptionNo);
    const conceptTitle = getVal(project.conceptTitle, project.conceptTitleNo);
    const role = getVal(project.role, project.roleNo);
    const conceptDesc = (language === 'no' && project.conceptDescriptionNo) ? project.conceptDescriptionNo : project.conceptDescription;

    const heroImg = (theme === 'dark' && project.heroImageDark) ? project.heroImageDark : project.heroImage;


    return (
        <div className="page-content project-detail">
            <Link to="/work" className="back-link">{t('nav.back')}</Link>

            {/* 1. Split Hero Section (Image Left, Text Right) */}
            <section className="detail-hero">
                <div className="hero-image">
                    <img src={heroImg} alt="Project Hero" />
                </div>
                <div className="hero-text">
                    <span className="detail-category">{category} | {project.year}</span>
                    <h1>{project.title}</h1>
                    <p className="hero-description">{description}</p>
                </div>
            </section>
            {project.fullImage && (
                <section className={`detail-full-image ${project.id === 6 ? 'rubi-full-image' : ''}`}>
                    <img src={project.fullImage} alt="Cinematic View" />
                    {project.fullImageCaption && (
                        <p className="image-caption">{getVal(project.fullImageCaption, project.fullImageCaptionNo)}</p>
                    )}
                </section>
            )}

            {/* 2. Main Body: Sticky Text + Scrolling Images Layout */}
            <section className="detail-body-split">

                {/* Sticky Sidebar for Text */}
                <div className="detail-sticky-info">
                    <h3>{conceptTitle}</h3>
                    {conceptDesc && conceptDesc.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                    ))}
                    <div className="project-specs">
                        <div className="spec-item">
                            <span className="label">{t('work.client')}</span>
                            <span className="value">{project.client}</span>
                        </div>
                        <div className="spec-item">
                            <span className="label">{t('work.role')}</span>
                            <span className="value">{role}</span>
                        </div>
                        {project.websiteUrl && (
                            <div className="spec-item">
                                <span className="label">{t('work.websiteLabel')}</span>
                                <a
                                    href={project.websiteUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="value website-link"
                                >
                                    {project.websiteUrl.replace(/^https?:\/\//, '')}
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Scrolling Image Stream */}
                <div className="detail-image-stream">
                    {project.gallery?.map((item, index) => (
                        <div key={index} className={`stream-item ${item.size}`}>
                            {item.type === "vimeo" ? (
                                <div className="vimeo-wrapper">
                                    <iframe
                                        src={`https://player.vimeo.com/video/${item.src}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1`}
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        title="Vimeo video"
                                    />
                                </div>
                            ) : item.type === "video" ? (
                                <video src={item.src} autoPlay loop muted playsInline controls />
                            ) : (
                                <img src={item.src} alt={`Detail ${index + 1}`} />
                            )}
                            {item.caption && (
                                <p className="image-caption">{getVal(item.caption, item.captionNo)}</p>
                            )}
                        </div>
                    ))}
                </div>

            </section>

            {/* 3. Full Width Cinematic Image */}
            {project.fullImage && (
                <section className={`detail-full-image ${project.id === 6 ? 'rubi-full-image' : ''}`}>
                    <img src={project.fullImage} alt="Cinematic View" />
                    {project.fullImageCaption && (
                        <p className="image-caption">{getVal(project.fullImageCaption, project.fullImageCaptionNo)}</p>
                    )}
                </section>
            )}

            {/* 4. Project Navigation */}
            <div className="project-navigation-container">
                <Link to={`/project/${prevProject.id}`} className="nav-project-btn">
                    ← {language === 'no' ? 'Forrige prosjekt' : 'Previous Project'}
                </Link>
                <Link to={`/project/${nextProject.id}`} className="nav-project-btn">
                    {language === 'no' ? 'Neste prosjekt' : 'Next Project'} →
                </Link>
            </div>
        </div>
    );
};

export default ProjectDetail;
